import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import {
  fetchItems,
  fetchCategories,
  addItem,
  updateItem,
  deleteItem,
  createCategory,
  updateCategory,
  deleteCategory,
  setSelectedCategory
} from '../../store/slices/inventorySlice';
import { Plus, Edit, Package, Search, AlertTriangle, Trash2, X, Image, Upload, Eye, Copy, Pencil, Scale, Download, Loader2, ArrowUpDown } from 'lucide-react';
import { exportInventoryToExcel } from '../../utils/excelExport';
import { formatProductUnitType } from '../../utils/unitLabels';
import apiService from '../../services/api';
import { isAdminOrOwner, isManagerOrAbove } from '../../lib/roles';
import { getImageUrl, formatSkuDisplay } from '../../utils/format';
import { createFuzzySearch } from '../../utils/searchUtils';
import { getTokens } from '../../utils/tokens';
import { getAdminSelectCSS, getAdminFormSelectCSS } from '../../utils/adminSelect';
import {
  normalizePurchaseQuantityInput,
  inventoryStockQuantityValid,
} from '../../utils/purchaseQuantity';
import {
  PageHeader,
  StatStrip,
  PrimaryBtn,
  GhostBtn,
  Toolbar,
  StatusPill,
  DataTable,
  Pagination,
  MonoMoney,
  AvatarInitials,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';

const Inventory = () => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [skuCopied, setSkuCopied] = useState(false);
  const [stockFilter, setStockFilter] = useState('all');
  const [lowStockThreshold, setLowStockThreshold] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [exporting, setExporting] = useState(false);

  const { items, categories, selectedCategory, loading, error } = useSelector((state) => state.inventory);
  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const { t, isRTL, language } = useLanguage();
  const showToast = useToast();
  const tk = getTokens(mode);

// ── Style helpers ────────────────────────────────────────────────────────────
const filterSelect = getAdminSelectCSS(tk, 'inv-filter');
const formSelect = getAdminFormSelectCSS(tk, 'inv-form');

const inputStyle = {
    width: '100%', height: 40, background: tk.sunken,
    border: `1px solid ${tk.border}`, borderRadius: 8,
    padding: '0 12px', color: tk.text, fontFamily: 'inherit',
    fontSize: 13, outline: 'none',
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: tk.textMuted,
    textTransform: 'uppercase', letterSpacing: 0.4,
    marginBottom: 6, display: 'block',
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  };

  const modalBox = {
    background: tk.surface, border: `1px solid ${tk.border}`,
    borderRadius: 14, padding: 0, maxWidth: 520,
    width: '100%', margin: '0 16px', maxHeight: '90vh',
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
    boxShadow: tk.shadowLg,
  };

  // ── Data loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchItems());
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    apiService.getStore().then((res) => {
      const data = res?.data || res;
      if (data?.low_stock_threshold != null) {
        setLowStockThreshold(data.low_stock_threshold);
      }
    }).catch(() => { });
  }, []);

  // ── Filtering & pagination ────────────────────────────────────────────────────
  const baseFiltered = useMemo(
    () =>
      (items || []).filter((item) => {
        const matchesCategory = !selectedCategory || item.category_id === selectedCategory;
        const stock = parseFloat(item.stock_quantity || 0);
        let matchesStock = true;
        if (stockFilter === 'low') matchesStock = stock > 0 && stock < (item.low_stock_threshold || lowStockThreshold || 10);
        else if (stockFilter === 'out') matchesStock = stock <= 0;
        else if (stockFilter === 'ok') matchesStock = stock >= (item.low_stock_threshold || lowStockThreshold || 10);
        return matchesCategory && matchesStock;
      }),
    [items, selectedCategory, stockFilter, lowStockThreshold]
  );

  const searchFn = useMemo(
    () => createFuzzySearch(baseFiltered, { keys: ['name', 'sku'], threshold: 0.4, ignoreLocation: true }),
    [baseFiltered]
  );
  const filteredItems = searchTerm.trim() ? searchFn(searchTerm) : baseFiltered;

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredItems, currentPage, itemsPerPage]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedCategory, stockFilter]);

  const hasActiveFilters =
    searchTerm.trim() !== '' || !!selectedCategory || stockFilter !== 'all';

  const resetFilters = () => {
    setSearchTerm('');
    setStockFilter('all');
    dispatch(setSelectedCategory(null));
  };

  // ── Derived stats ─────────────────────────────────────────────────────────────
  // Stats respect the active filters / search, not the whole catalogue.
  const inventoryStats = useMemo(() => {
    let stockValue = 0;
    let lowStockCount = 0;
    let outOfStockCount = 0;
    for (const item of filteredItems) {
      const stock = parseFloat(item.stock_quantity || 0);
      const cost = parseFloat(item.cost_price || 0);
      stockValue += stock * cost;
      const threshold = item.low_stock_threshold || lowStockThreshold || 10;
      if (stock <= 0) outOfStockCount += 1;
      else if (stock < threshold) lowStockCount += 1;
    }
    return {
      totalItems: filteredItems.length,
      stockValue,
      lowStockCount,
      outOfStockCount,
    };
  }, [filteredItems, lowStockThreshold]);
  const { stockValue, lowStockCount, outOfStockCount } = inventoryStats;

  const canManageInventory = isManagerOrAbove(user?.role);

  // The API returns `errorCode` (not `code`) on delete-blocked responses.
  const getProductDeleteErrorMessage = (e) => {
    const raw = (typeof e === 'string' ? e : e?.error ?? e?.message) || '';
    const errorCode = e?.errorCode ?? e?.code;
    if (errorCode === 'PRODUCT_IN_OFFER') return t('alerts.productInOffer');
    if (errorCode === 'PRODUCT_HAS_PURCHASES') return t('alerts.productHasPurchases');
    if (errorCode === 'PRODUCT_REFERENCED') return t('alerts.productReferenced');
    if (errorCode === 'CATEGORY_HAS_PRODUCTS_IN_OFFERS') return t('alerts.categoryHasProductsInOffers');
    if (errorCode === 'CATEGORY_HAS_PRODUCTS_WITH_PURCHASES') return t('alerts.categoryHasProductsWithPurchases');
    if (raw.includes('offer_items_product_id_fkey')) return t('alerts.categoryHasProductsInOffers');
    if (raw.includes('purchases_product_id_fkey')) return t('alerts.productPurchaseRestrict');
    return raw || t('alerts.deleteFailed');
  };

  // ── ItemModal ─────────────────────────────────────────────────────────────────
  const ItemModal = ({ item, onClose, onSave }) => {
    const [formData, setFormData] = useState(
      item ? {
        ...item,
        cost_price: item.cost_price ?? '',
        selling_price: item.price ?? item.selling_price ?? '',
        stock_quantity: item.stock_quantity ?? '0',
        unit_type: (item.unit_type === 'kg' ? 'weight' : item.unit_type) || 'piece',
        image_url: item.image_url ?? item.image_path ?? '',
      } : {
        name: '',
        cost_price: '',
        selling_price: '',
        category_id: '',
        stock_quantity: '0',
        image_url: '',
        sku: '',
        unit_type: 'piece',
      }
    );
    const [saving, setSaving] = useState(false);

    const calculateProfitMargin = () => {
      const cost = parseFloat(formData.cost_price) || 0;
      const selling = parseFloat(formData.selling_price) || 0;
      return (selling - cost).toFixed(2);
    };

    const hasChanges = (() => {
      if (!item) return true;
      const normUnit = (u) => (u === 'kg' ? 'weight' : u) || 'piece';
      const num = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      return (
        (formData.name || '').trim() !== (item.name || '').trim() ||
        num(formData.cost_price) !== num(item.cost_price) ||
        num(formData.selling_price) !== num(item.price ?? item.selling_price) ||
        num(formData.stock_quantity) !== num(item.stock_quantity ?? 0) ||
        normUnit(formData.unit_type) !== normUnit(item.unit_type) ||
        String(formData.category_id || '') !== String(item.category_id || '') ||
        (formData.sku || '').trim() !== (item.sku || '').trim() ||
        (formData.image_url || '') !== (item.image_url ?? item.image_path ?? '')
      );
    })();

    const handleSubmit = async (e) => {
      if (e) e.preventDefault();
      if (item && !hasChanges) return;
      setSaving(true);
      try {
        if (!formData.name || !formData.name.trim()) throw new Error(t('alerts.itemNameRequired'));
        if (formData.name.trim().length > 40) throw new Error(t('alerts.productNameMaxLength'));
        const nameTrimmed = formData.name.trim();
        const duplicateName = (items || []).find((i) => i.name && i.name.trim().toLowerCase() === nameTrimmed.toLowerCase() && i.id !== item?.id);
        if (duplicateName) throw new Error(t('inventory.productNameAlreadyExists'));
        if (!formData.category_id || !String(formData.category_id).trim()) throw new Error(t('alerts.categoryRequired'));
        if (formData.sku && formData.sku.trim().length > 50) throw new Error(t('alerts.productSkuMaxLength'));
        const effectiveSku = formData.sku?.trim() || null;
        if (effectiveSku) {
          const duplicate = (items || []).find((i) => i.sku === effectiveSku && i.id !== item?.id);
          if (duplicate) throw new Error(t('inventory.skuAlreadyExists'));
        }
        const stockQty = parseFloat(formData.stock_quantity);
        if (isNaN(stockQty) || stockQty < 0) throw new Error(t('alerts.stockQuantityInvalid'));
        if (!inventoryStockQuantityValid(stockQty, formData.unit_type)) {
          throw new Error(
            formData.unit_type === 'weight'
              ? t('alerts.purchaseQuantityKgMaxDecimals')
              : t('alerts.purchaseQuantityPieceWholeNumber'),
          );
        }
        const costPrice = parseFloat(formData.cost_price) || 0;
        const sellingPrice = parseFloat(formData.selling_price) || 0;
        if (costPrice < 0) throw new Error(t('alerts.costPriceNegative'));
        if (sellingPrice < 0) throw new Error(t('alerts.sellingPriceNegative'));
        if (sellingPrice < costPrice) throw new Error(t('alerts.profitMarginNegative'));
        const submitData = {
          ...formData,
          name: nameTrimmed,
          cost_price: costPrice,
          selling_price: sellingPrice,
          stock_quantity: stockQty,
          category_id: formData.category_id || null,
          image_url: formData.image_url || null,
          sku: formData.sku || null,
          unit_type: formData.unit_type || 'piece',
        };
        if (item) {
          await dispatch(updateItem({ ...submitData, id: item.id })).unwrap();
        } else {
          await dispatch(addItem(submitData)).unwrap();
        }
        onSave();
      } catch (err) {
        const errMsg = typeof err === 'string' ? err : (err?.message || err?.body?.error);
        const raw = String(errMsg || '');
        const isProductPurchaseRestrict = raw.includes('purchases_product_id_fkey') || (raw.includes('violates') && raw.includes('RESTRICT') && raw.includes('products'));
        let showError = t('alerts.failedToSave').replace('{error}', errMsg || t('alerts.unknownError'));
        if (errMsg && String(errMsg).includes('SKU') && String(errMsg).includes('already exists')) showError = t('inventory.skuAlreadyExists');
        else if (errMsg && String(errMsg).includes('product with this name already exists')) showError = t('inventory.productNameAlreadyExists');
        else if (isProductPurchaseRestrict) showError = t('alerts.productPurchaseRestrict');
        else if (raw.includes('Selling price must be greater than or equal to cost price')) showError = t('alerts.profitMarginNegative');
        else if (raw === 'Quantity for piece products must be a whole number') showError = t('alerts.purchaseQuantityPieceWholeNumber');
        else if (raw === 'Kg quantity may have at most 3 decimal places') showError = t('alerts.purchaseQuantityKgMaxDecimals');
        else if (raw === 'Stock quantity must be 0 or greater') showError = t('alerts.stockQuantityInvalid');
        showToast(showError, "error");
      }
      setSaving(false);
    };

    return (
      <div style={overlay}>
        <div style={modalBox}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: `1px solid ${tk.border}`,
            background: tk.surface,
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {item ? t('inventory.editItem') : t('inventory.addItem')}
            </span>
            <button
              type="button"
              onClick={onClose}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6,
                background: 'transparent', border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: 'pointer',
              }}
            >
              <X size={14} />
            </button>
          </div>

          <form onSubmit={handleSubmit} style={{ padding: '20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Product name */}
            <div>
              <label style={labelStyle}>{t('inventory.itemName')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={40}
                style={inputStyle}
                required
              />
            </div>

            {/* Cost & selling price */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>{t('inventory.costPrice')} ({t('currency.egp')})</label>
                <input
                  type="number"
                  value={formData.cost_price}
                  onChange={(e) => {
                    if (e.target.value !== '' && parseFloat(e.target.value) > 999999.99) return;
                    setFormData({ ...formData, cost_price: e.target.value });
                  }}
                  style={inputStyle}
                  step="0.01" min="0" max="999999.99"
                />
              </div>
              <div>
                <label style={labelStyle}>{t('inventory.sellingPrice')} ({t('currency.egp')})</label>
                <input
                  type="number"
                  value={formData.selling_price}
                  onChange={(e) => {
                    if (e.target.value !== '' && parseFloat(e.target.value) > 999999.99) return;
                    setFormData({ ...formData, selling_price: e.target.value });
                  }}
                  style={inputStyle}
                  step="0.01" min="0" max="999999.99"
                />
              </div>
            </div>

            {/* Profit margin (read-only) */}
            <div>
              <label style={labelStyle}>{t('inventory.profitMargin')} ({t('currency.egp')})</label>
              <input
                type="number"
                value={calculateProfitMargin()}
                readOnly
                style={{ ...inputStyle, opacity: 0.7, cursor: 'default' }}
              />
            </div>

            {/* Unit type */}
            <div>
              <label style={labelStyle}>{t('inventory.unitType')}</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {[
                  { value: 'piece', label: t('inventory.perPiece'), Icon: Package },
                  { value: 'weight', label: t('inventory.perKg'), Icon: Scale },
                ].map(({ value, label, Icon }) => {
                  const active = formData.unit_type === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        const unit_type = value;
                        const stock_quantity = normalizePurchaseQuantityInput(formData.stock_quantity, unit_type);
                        setFormData({ ...formData, unit_type, stock_quantity });
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        height: 38,
                        borderRadius: 9,
                        border: `1px solid ${active ? tk.accent : tk.border}`,
                        background: active ? tk.accent : 'transparent',
                        color: active ? '#fff' : tk.textSecondary,
                        fontSize: 12,
                        fontWeight: active ? 600 : 400,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        transition: 'all 0.15s',
                        boxShadow: active ? `0 2px 8px ${tk.accent}40` : 'none',
                      }}
                    >
                      <Icon size={14} />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stock quantity */}
            <div>
              <label style={labelStyle}>{t('inventory.stockQuantity')}</label>
              <input
                type="number"
                value={formData.stock_quantity}
                onChange={(e) => {
                  const next = normalizePurchaseQuantityInput(e.target.value, formData.unit_type);
                  if (next !== '' && parseFloat(next) > 99999999.99) return;
                  setFormData({ ...formData, stock_quantity: next });
                }}
                style={inputStyle}
                min="0" max="99999999.99"
                step={formData.unit_type === 'weight' ? '0.001' : '1'}
              />
            </div>

            {/* Category */}
            <div>
              <label style={labelStyle}>{t('inventory.category')}</label>
<select
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className={formSelect.className}
                    required
                  >
                    <option value="">{t('inventory.selectCategory')}</option>
                    {(categories || []).map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
            </div>

            {/* SKU */}
            <div>
              <label style={labelStyle}>{t('inventory.skuOptional')}</label>
              <input
                type="text"
                value={formData.sku || ''}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                maxLength={50}
                style={inputStyle}
              />
            </div>

            {/* Image */}
            <div>
              <label style={labelStyle}>{t('inventory.imageOptional')}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {formData.image_url && (
                  <div style={{ position: 'relative', width: '100%', height: 120, borderRadius: 8, overflow: 'hidden', border: `1px solid ${tk.border}`, background: tk.sunken }}>
                    <img
                      src={getImageUrl(formData.image_url)}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div style={{ position: 'absolute', inset: 0, display: 'none', alignItems: 'center', justifyContent: 'center', background: tk.sunken, flexDirection: 'column', gap: 4 }}>
                      <Image size={24} style={{ color: tk.textMuted }} />
                      <p style={{ fontSize: 11, color: tk.textMuted }}>{t('inventory.invalidImage')}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      style={{
                        position: 'absolute', top: 6, right: 6,
                        width: 22, height: 22, borderRadius: '50%',
                        background: tk.danger, border: 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <X size={12} style={{ color: '#fff' }} />
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    value={formData.image_url || ''}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder={t('inventory.enterImageUrl')}
                  />
                  <label style={{
                    height: 40, padding: '0 12px', borderRadius: 8,
                    background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                    color: tk.textSecondary, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontFamily: 'inherit', fontSize: 12.5, whiteSpace: 'nowrap',
                  }}>
                    <Upload size={14} />
                    <span>{t('inventory.browse')}</span>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const reader = new FileReader();
                            reader.onloadend = async () => {
                              try {
                                const res = await apiService.uploadImage(reader.result, file.name, 'products');
                                setFormData({ ...formData, image_url: res.url || res.path });
                              } catch {
                                setFormData({ ...formData, image_url: reader.result });
                              }
                            };
                            reader.readAsDataURL(file);
                          } catch (err) {
                            console.error('Failed to save image:', err);
                          }
                        }
                      }}
                    />
                  </label>
                </div>
                <p style={{ fontSize: 11, color: tk.textMuted }}>{t('inventory.imageUrlHelp')}</p>
              </div>
            </div>
          </form>

          <div style={{
            display: 'flex', gap: 10, padding: '14px 20px',
            borderTop: `1px solid ${tk.border}`, background: tk.surface,
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                flex: 1, height: 38, borderRadius: 8,
                background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || (item && !hasChanges)}
              style={{
                flex: 1, height: 38, borderRadius: 8,
                background: tk.accent, border: 'none',
                color: '#fff', cursor: (saving || (item && !hasChanges)) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                opacity: (saving || (item && !hasChanges)) ? 0.6 : 1,
              }}
            >
              {saving ? t('inventory.saving') : (item ? t('common.update') : t('inventory.addItem'))}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── CategoryManagementModal ───────────────────────────────────────────────────
  const CategoryManagementModal = ({ onClose, onSave }) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingCategoryLocal, setEditingCategoryLocal] = useState(null);
    const [deletingCategoryLocal, setDeletingCategoryLocal] = useState(null);
    const [deleteCategoryConfirmText, setDeleteCategoryConfirmText] = useState('');

    const CategoryForm = ({ category, onClose: formClose, onSave: formSave }) => {
      const [formData, setFormData] = useState(category || { name: '', color: '#6B7280' });
      const [saving, setSaving] = useState(false);

      const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name?.trim()) return;
        if (formData.name.trim().length > 30) {
          showToast(t('alerts.inventoryCategoryNameMaxLength'), "warning");
          return;
        }
        const nameTrimmed = formData.name.trim();
        const duplicate = (categories || []).find((c) => c.name.toLowerCase() === nameTrimmed.toLowerCase() && c.id !== category?.id);
        if (duplicate) {
          showToast(t('inventory.categoryNameUnique'), "warning");
          return;
        }
        setSaving(true);
        try {
          if (category) await dispatch(updateCategory({ id: category.id, categoryData: formData })).unwrap();
          else await dispatch(createCategory(formData)).unwrap();
          await dispatch(fetchItems());
          formSave();
        } catch (err) {
          const errMsg = typeof err === 'string' ? err : (err?.message || err?.body?.error);
          const showError = errMsg && String(errMsg).includes('already exists')
            ? t('inventory.categoryNameUnique')
            : t('alerts.failedToSaveCategory').replace('{error}', errMsg || t('alerts.unknownError'));
          showToast(showError, "error");
        }
        setSaving(false);
      };

      return (
        <div style={{
          background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
          borderRadius: 10, padding: 16,
        }}>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: tk.text, marginBottom: 14, marginTop: 0 }}>
            {category ? t('inventory.editCategory') : t('inventory.addNewCategory')}
          </h4>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={labelStyle}>{t('inventory.categoryName')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={30}
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>{t('inventory.color')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="color"
                  value={formData.color || '#6B7280'}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  style={{ width: 44, height: 36, borderRadius: 6, border: `1px solid ${tk.border}`, cursor: 'pointer', padding: 2 }}
                />
                <input
                  type="text"
                  value={formData.color || ''}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="#6B7280"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
              <button
                type="button"
                onClick={formClose}
                disabled={saving}
                style={{
                  flex: 1, height: 34, borderRadius: 8,
                  background: tk.surface, border: `1px solid ${tk.border}`,
                  color: tk.textSecondary, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  flex: 1, height: 34, borderRadius: 8,
                  background: tk.accent, border: 'none',
                  color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? t('inventory.saving') : (category ? t('common.update') : t('inventory.addCategory'))}
              </button>
            </div>
          </form>
        </div>
      );
    };

    return (
      <div style={overlay}>
        <div style={{
          background: tk.surface, border: `1px solid ${tk.border}`,
          borderRadius: 14, width: '100%', maxWidth: 560,
          margin: '0 16px', display: 'flex', flexDirection: 'column',
          maxHeight: '80vh', overflow: 'hidden', boxShadow: tk.shadowLg,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: `1px solid ${tk.border}`,
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {t('inventory.categoryManagement')}
            </span>
            <button
              onClick={onClose}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6,
                background: 'transparent', border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: 'pointer',
              }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
            {(showAddForm || editingCategoryLocal) && (
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${tk.border}` }}>
                <CategoryForm
                  category={editingCategoryLocal}
                  onClose={() => { setShowAddForm(false); setEditingCategoryLocal(null); }}
                  onSave={() => {
                    setShowAddForm(false);
                    setEditingCategoryLocal(null);
                    dispatch(fetchCategories());
                    dispatch(fetchItems());
                  }}
                />
              </div>
            )}

            {!showAddForm && !editingCategoryLocal && (
              <button
                onClick={() => setShowAddForm(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  height: 34, padding: '0 14px', borderRadius: 8, marginBottom: 12,
                  background: tk.accentSoft, border: 'none',
                  color: tk.accentText, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                }}
              >
                <Plus size={13} />
                {t('inventory.addNewCategory')}
              </button>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(categories || []).length === 0 ? (
                <p style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 13 }}>
                  {t('inventory.noCategories')}
                </p>
              ) : (
                (categories || []).map((cat) => (
                  <div
                    key={cat.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 8,
                      border: `1px solid ${tk.border}`, background: tk.surfaceAlt,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', background: cat.color || '#6B7280', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 500, color: tk.text }}>{cat.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => setEditingCategoryLocal(cat)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, borderRadius: 6,
                          background: tk.accentSoft, border: 'none',
                          color: tk.accentText, cursor: 'pointer',
                        }}
                      >
                        <Edit size={13} />
                      </button>
                      <button
                        onClick={() => setDeletingCategoryLocal(cat)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, borderRadius: 6,
                          background: tk.dangerSoft, border: 'none',
                          color: tk.dangerText, cursor: 'pointer',
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Delete category confirm (nested) */}
            {deletingCategoryLocal && (
              <div style={{ ...overlay, zIndex: 60 }}>
                <div style={{
                  background: tk.surface, border: `1px solid ${tk.border}`,
                  borderRadius: 14, width: '100%', maxWidth: 400,
                  margin: '0 16px', padding: '24px', boxShadow: tk.shadowLg,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: tk.text, marginBottom: 10 }}>
                    {t('inventory.deleteCategory')}
                  </div>
                  <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
                    {t('common.delete')} "{deletingCategoryLocal?.name}"{t('common.questionMark')} {t('inventory.deleteCategoryConfirm')}
                  </div>
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 12, color: tk.textMuted, marginBottom: 6, fontWeight: 600 }}>
                      Type <span style={{ fontFamily: '"JetBrains Mono", monospace', color: tk.dangerText, letterSpacing: 1 }}>DELETE</span> to confirm
                    </div>
                    <input
                      type="text"
                      value={deleteCategoryConfirmText}
                      onChange={(e) => setDeleteCategoryConfirmText(e.target.value)}
                      placeholder="DELETE"
                      autoComplete="off"
                      style={{
                        width: '100%', height: 36, borderRadius: 8,
                        border: `1px solid ${deleteCategoryConfirmText === 'DELETE' ? tk.danger : tk.border}`,
                        background: tk.sunken, color: tk.text,
                        fontFamily: '"JetBrains Mono", monospace', fontSize: 13,
                        padding: '0 10px', outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => { setDeletingCategoryLocal(null); setDeleteCategoryConfirmText(''); }}
                      style={{
                        flex: 1, height: 38, borderRadius: 8,
                        background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                        color: tk.textSecondary, cursor: 'pointer',
                        fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                      }}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      disabled={deleteCategoryConfirmText !== 'DELETE'}
                      onClick={async () => {
                        try {
                          await dispatch(deleteCategory(deletingCategoryLocal.id)).unwrap();
                          dispatch(fetchCategories());
                          dispatch(fetchItems());
                          setDeletingCategoryLocal(null);
                          setDeleteCategoryConfirmText('');
                        } catch (e) {
                          showToast(getProductDeleteErrorMessage(e), "warning");
                        }
                      }}
                      style={{
                        flex: 1, height: 38, borderRadius: 8,
                        background: deleteCategoryConfirmText === 'DELETE' ? tk.danger : tk.surfaceAlt,
                        border: `1px solid ${deleteCategoryConfirmText === 'DELETE' ? tk.danger : tk.border}`,
                        color: deleteCategoryConfirmText === 'DELETE' ? '#fff' : tk.textMuted,
                        cursor: deleteCategoryConfirmText === 'DELETE' ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                        transition: 'all 0.15s',
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── DeleteConfirmModal ────────────────────────────────────────────────────────
  const DeleteConfirmModal = ({ item: itemToDelete, onClose, onConfirm }) => (
    <div style={overlay}>
      <div style={{
        background: tk.surface, border: `1px solid ${tk.border}`,
        borderRadius: 14, width: '100%', maxWidth: 400,
        margin: '0 16px', padding: '24px', boxShadow: tk.shadowLg,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: tk.text, marginBottom: 10 }}>
          {t('inventory.deleteItem')}
        </div>
        <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 22, lineHeight: 1.5 }}>
          {t('common.delete')} "{itemToDelete?.name}"{t('common.questionMark')} {t('inventory.deleteItemConfirm')}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, height: 38, borderRadius: 8,
              background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
              color: tk.textSecondary, cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => { onConfirm(itemToDelete.id); onClose(); }}
            style={{
              flex: 1, height: 38, borderRadius: 8,
              background: tk.danger, border: 'none',
              color: '#fff', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
            }}
          >
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );

  // ── QuickStockAdjustModal ─────────────────────────────────────────────────────
  const QuickStockAdjustModal = ({ item, onClose, onSaved }) => {
    const [delta, setDelta] = useState('');
    const [saving, setSaving] = useState(false);
    const currentStock = parseFloat(item.stock_quantity || 0);
    const isWeight = item.unit_type === 'weight';
    const deltaNum = parseFloat(delta) || 0;
    const rawNewStock = currentStock + deltaNum;
    const newStock = Math.max(0, rawNewStock);
    const willClamp = rawNewStock < 0;

    const applyDelta = (amount) => {
      setDelta((prev) => {
        const cur = parseFloat(prev) || 0;
        const next = Math.round((cur + amount) * 1000) / 1000;
        return next === 0 ? '' : String(next);
      });
    };

    const stepBtnStyle = {
      width: 34, height: 36, flexShrink: 0, borderRadius: 8,
      background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
      color: tk.text, cursor: 'pointer', fontFamily: 'inherit',
      fontSize: 13, fontWeight: 600,
      direction: 'ltr', unicodeBidi: 'isolate',
    };

    const chipStyle = {
      flex: 1, height: 30, borderRadius: 8,
      background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
      color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit',
      fontSize: 12, fontWeight: 600,
      direction: 'ltr', unicodeBidi: 'isolate',
    };

    const handleSave = async () => {
      if (deltaNum === 0) { onClose(); return; }
      setSaving(true);
      try {
        await dispatch(updateItem({ id: item.id, stock_quantity: newStock })).unwrap();
        showToast(t('inventory.stockAdjusted'), 'success');
        onSaved();
      } catch (e) {
        const msg = typeof e === 'string' ? e : (e?.message || e?.error);
        showToast(msg || t('alerts.failedToSave'), 'warning');
      }
      setSaving(false);
    };

    const sku = formatSkuDisplay(item.sku);
    const imageSrc = item.image_url || item.image_path;

    return (
      <div style={overlay}>
        <div style={{
          background: tk.surface, border: `1px solid ${tk.border}`,
          borderRadius: 14, width: '100%', maxWidth: 450,
          margin: '0 16px', padding: 0, boxShadow: tk.shadowLg,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: `1px solid ${tk.border}`,
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {t('inventory.adjustStock')}
            </span>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6,
                background: 'transparent', border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              <X size={14} />
            </button>
          </div>
          <div style={{ padding: 22 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            marginBottom: 14,
          }}>
            <div style={{
              width: 69, height: 69, borderRadius: 8, flexShrink: 0,
              background: tk.sunken, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${tk.border}`,
            }}>
              {imageSrc
                ? <img src={getImageUrl(imageSrc)} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Package size={29} style={{ color: tk.textMuted }} />
              }
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: 13.5, fontWeight: 600, color: tk.text,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {item.name}
              </div>
              <div style={{
                marginTop: 3,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11.5,
                color: tk.textSecondary,
              }}>
                {item.category_color && (
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.category_color, flexShrink: 0 }} />
                )}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.category_name || '—'}
                </span>
              </div>
              {item.sku && String(item.sku).trim() && (
                <div
                  title={sku.title}
                  style={{
                    marginTop: 3,
                    fontSize: 11.5,
                    color: tk.textMuted,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                    direction: 'ltr',
                    unicodeBidi: 'isolate',
                    justifyContent: isRTL ? 'flex-end' : 'flex-start',
                    width: '100%',
                  }}
                >
                  <span>SKU:</span>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace' }}>
                    {sku.display}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 12px', background: tk.surfaceAlt, borderRadius: 8,
            marginBottom: 14, fontSize: 12.5,
          }}>
            <span style={{ color: tk.textMuted }}>{t('inventory.currentStock')}</span>
            <span style={{
              fontFamily: '"JetBrains Mono", monospace', fontWeight: 700,
              color: currentStock < 0 ? tk.dangerText : tk.text,
              direction: 'ltr', unicodeBidi: 'isolate', display: 'inline-block',
            }}>
              {currentStock} {isWeight ? 'kg' : 'pcs'}
            </span>
          </div>

          <label style={labelStyle}>{t('inventory.adjustBy')}</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <button type="button" onClick={() => applyDelta(-1)} style={stepBtnStyle}>−1</button>
            <input
              type="text"
              inputMode="decimal"
              value={delta}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || v === '-' || /^-?\d*\.?\d*$/.test(v)) setDelta(v);
              }}
              placeholder="0"
              autoFocus
              style={{ ...inputStyle, textAlign: 'center', flex: 1 }}
            />
            <button type="button" onClick={() => applyDelta(1)} style={stepBtnStyle}>+1</button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {[5, 10, 50].map((n) => (
              <button key={n} type="button" onClick={() => applyDelta(n)} style={chipStyle}>+{n}</button>
            ))}
          </div>

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 12px', background: tk.surfaceAlt, borderRadius: 8,
            marginBottom: willClamp ? 8 : 18, fontSize: 12.5,
          }}>
            <span style={{ color: tk.textMuted }}>{t('inventory.newStock')}</span>
            <span style={{
              fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, color: tk.successText,
              direction: 'ltr', unicodeBidi: 'isolate', display: 'inline-block',
            }}>
              {newStock} {isWeight ? 'kg' : 'pcs'}
            </span>
          </div>
          {willClamp && (
            <div style={{ fontSize: 11, color: tk.warning, marginBottom: 18, lineHeight: 1.4 }}>
              {t('inventory.stockCannotGoBelowZero')}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                flex: 1, height: 38, borderRadius: 8,
                background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || deltaNum === 0}
              style={{
                flex: 1, height: 38, borderRadius: 8,
                background: tk.accent, border: 'none',
                color: '#fff', cursor: (saving || deltaNum === 0) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                opacity: (saving || deltaNum === 0) ? 0.6 : 1,
              }}
            >
              {saving ? t('inventory.saving') : t('common.save')}
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Table columns ─────────────────────────────────────────────────────────────
  const columns = [
    { key: 'sku', label: t('inventory.sku') || 'SKU' },
    {
      key: 'name',
      label: language === 'ar'
        ? <span style={{ display: 'inline-block', paddingRight: 20 }}>اسم العنصر</span>
        : t('inventory.itemName') || 'Product',
    },
    { key: 'category_name', label: t('inventory.category') || 'Category' },
    { key: 'cost_price', label: t('inventory.costPrice') || 'Cost' },
    { key: 'price', label: t('inventory.sellingPrice') || 'Price' },
    { key: 'margin', label: t('inventory.profitMargin') || 'Margin' },
    { key: 'stock_quantity', label: t('inventory.stockQuantity') || 'Stock' },
    { key: 'actions', label: '' },
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case 'sku':
        return (
          <span
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 11.5,
              color: tk.textMuted,
              display: 'inline-block',
              paddingInlineEnd: 12,
            }}
          >
            {row.sku || '—'}
          </span>
        );
      case 'name':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingInlineStart: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 6, flexShrink: 0,
              background: tk.sunken, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {row.image_url
                ? <img src={getImageUrl(row.image_url)} alt={row.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Package size={14} style={{ color: tk.textMuted }} />
              }
            </div>
            <span style={{ fontWeight: 500, color: tk.text, fontSize: 13 }}>{row.name || row.product_name}</span>
          </div>
        );
      case 'category_name':
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {row.category_color && (
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: row.category_color, flexShrink: 0 }} />
            )}
            <span style={{ fontSize: 11, color: tk.textSecondary }}>{row.category_name || '—'}</span>
          </span>
        );
      case 'cost_price':
        return <MonoMoney amount={parseFloat(row.cost_price || 0)} tk={tk} size={12} weight={500} color={tk.textSecondary} />;
      case 'price':
        return <MonoMoney amount={parseFloat(row.price || row.selling_price || 0)} tk={tk} size={12.5} weight={600} />;
      case 'margin': {
        const price = parseFloat(row.price || row.selling_price || 0);
        const cost = parseFloat(row.cost_price || 0);
        const m = price > 0 ? ((price - cost) / price * 100).toFixed(0) + '%' : '—';
        return <span style={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: tk.successText, fontSize: 12 }}>{m}</span>;
      }
      case 'stock_quantity': {
        const stock = parseFloat(row.stock_quantity || 0);
        const threshold = row.low_stock_threshold || lowStockThreshold || 10;
        const isNegative = stock < 0;
        const isOut = stock <= 0;
        const isLow = stock > 0 && stock < threshold;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
            <span style={{
              fontFamily: '"JetBrains Mono", monospace', fontWeight: 700,
              color: isOut ? tk.dangerText : isLow ? tk.warning : tk.text,
              fontSize: 13,
              direction: 'ltr', unicodeBidi: 'isolate', display: 'inline-block',
            }}>
              {stock} {row.unit_type === 'weight' ? 'kg' : 'pcs'}
            </span>
            {(isOut || isLow) && (
              <StatusPill tk={tk} kind={isOut ? 'cancelled' : 'pending'}>
                {isNegative ? t('inventory.oversold') : isOut ? 'Out' : 'Low'}
              </StatusPill>
            )}
          </div>
        );
      }
      case 'actions':
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => setViewingItem(row)}
              style={{
                width: 28, height: 28, background: tk.surfaceAlt,
                border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: tk.textSecondary,
              }}
              title={t('common.view')}
            >
              <Eye size={13} />
            </button>
            {canManageInventory && (
              <>
                <button
                  onClick={() => setAdjustingItem(row)}
                  style={{
                    width: 28, height: 28, flexShrink: 0, background: tk.warningSoft,
                    border: 'none', borderRadius: 6, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: tk.warning,
                  }}
                  title={t('inventory.adjustStock')}
                >
                  <ArrowUpDown size={13} />
                </button>
                <button
                  onClick={() => setEditingItem(row)}
                  style={{
                    width: 28, height: 28, background: tk.accentSoft,
                    border: 'none', borderRadius: 6, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: tk.accentText,
                  }}
                  title={t('common.edit')}
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => setDeletingItem(row)}
                  style={{
                    width: 28, height: 28, background: tk.dangerSoft,
                    border: `1px solid ${tk.danger}33`, borderRadius: 6, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: tk.dangerText,
                  }}
                  title={t('common.delete')}
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
        );
      default:
        return <span style={{ color: tk.textSecondary, fontSize: 12 }}>{row[key] || '—'}</span>;
    }
  };

  const handleExport = async () => {
    if (!filteredItems.length) return;
    setExporting(true);
    try {
      const labels = t('inventory.excelExport');
      const L = typeof labels === 'object' && labels !== null ? labels : {};
      const today = new Date().toISOString().split('T')[0];
      await exportInventoryToExcel(filteredItems, `inventory-${today}.xlsx`, {
        ...L,
        formatUnitType: (u) => formatProductUnitType(u, t),
        categoryColorByName: Object.fromEntries(
          (categories || [])
            .filter((c) => c && String(c.name || '').trim() && c.color)
            .map((c) => [String(c.name).trim(), String(c.color).trim()])
        ),
      });
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExporting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  const wrapperStyle = {
    background: tk.bg, color: tk.text, padding: 20,
    minHeight: '100%', fontFamily: language === 'ar' ? '"Cairo",sans-serif' : '"DM Sans",sans-serif',
    overflowY: 'auto',
  };

  const lowStockItems = (items || []).filter(item => parseFloat(item.stock_quantity || 0) < lowStockThreshold);

  return (
    <div style={wrapperStyle} className={`theme-${mode}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <style>{filterSelect.css}{formSelect.css}</style>
      <PageHeader
        tk={tk}
        title={t('inventory.inventoryManagement')}
        subtitle={t('inventory.manageStock') || 'Products with live stock levels'}
        actions={
          <>
            <GhostBtn tk={tk} icon={exporting ? Loader2 : Download} onClick={handleExport}>
              {exporting ? t('monthlyClosing.downloading') : t('inventory.downloadExcel')}
            </GhostBtn>
            {canManageInventory && (
              <GhostBtn tk={tk} onClick={() => setShowCategoryModal(true)} icon={Package}>
                {t('inventory.manageCategories')}
              </GhostBtn>
            )}
            {canManageInventory && (
              <PrimaryBtn tk={tk} onClick={() => setShowAddModal(true)} icon={Plus}>
                {t('inventory.addItem')}
              </PrimaryBtn>
            )}
          </>
        }
      />

      {loading ? (
        <StatStripSkeleton tk={tk} count={4} />
      ) : (
        <StatStrip
          tk={tk}
          items={[
            { label: language === 'ar' ? 'إجمالي الأصناف' : 'Total Items', value: String(inventoryStats.totalItems) },
            { label: language === 'ar' ? 'قيمة المخزون' : 'Stock value', value: stockValue, money: true },
            {
              label: language === 'ar' ? 'مخزون منخفض' : 'Low stock',
              value: String(lowStockCount),
              delta: language === 'ar' ? 'أعد الطلب قريباً' : 'Reorder soon',
              positive: false,
            },
            {
              label: language === 'ar' ? 'نفد المخزون' : 'Out of stock',
              value: String(outOfStockCount),
              delta: language === 'ar' ? 'حرج' : 'Critical',
              positive: false,
            },
          ]}
        />
      )}

      {error && (
        <div style={{
          background: tk.dangerSoft, border: `1px solid ${tk.danger}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          color: tk.dangerText, fontSize: 12.5,
        }}>
          {t('common.error')}: {error}
        </div>
      )}

      {lowStockItems.length > 0 && (
        <div style={{
          background: tk.warningSoft, border: `1px solid ${tk.warning}44`,
          borderRadius: 10, padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <AlertTriangle size={16} style={{ color: tk.warning, flexShrink: 0 }} />
          <div>
            <p style={{ fontWeight: 600, fontSize: 12.5, color: tk.warningText, margin: 0 }}>
              {lowStockItems.length} {t('inventory.lowStockWarning').replace('< 10', `< ${lowStockThreshold}`)}
            </p>
            <p style={{ fontSize: 11.5, color: tk.warningText, opacity: 0.85, margin: '2px 0 0' }}>
              {lowStockItems.slice(0, 5).map(i => i.name).join(', ')}
              {lowStockItems.length > 5 ? ` ${t('inventory.andMore').replace('{count}', lowStockItems.length - 5)}` : ''}
            </p>
          </div>
        </div>
      )}

      <Toolbar
        tk={tk}
        searchPlaceholder={t('inventory.searchProducts') || 'Search products...'}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        filters={
          <>
<select
                  value={selectedCategory || 'all'}
                  onChange={(e) => dispatch(setSelectedCategory(e.target.value === 'all' ? null : e.target.value))}
                  className={filterSelect.className(selectedCategory && selectedCategory !== 'all')}
                  data-active={filterSelect.dataActive(selectedCategory && selectedCategory !== 'all')}
                >
                  <option value="all">{t('inventory.allCategories')}</option>
                  {(categories || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                  className={filterSelect.className(stockFilter !== 'all')}
                  data-active={filterSelect.dataActive(stockFilter !== 'all')}
                >
                  <option value="all">{t('inventory.allProducts')}</option>
                  <option value="low">{t('inventory.lowStockOnly')}</option>
                  <option value="out">Out of stock</option>
                  <option value="ok">In stock</option>
                </select>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    style={{
                      alignSelf: 'flex-end',
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      height: 34, padding: '0 10px', borderRadius: 8,
                      background: tk.dangerSoft, border: 'none',
                      color: tk.dangerText, fontSize: 12, fontWeight: 500,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <X size={12} />
                    {t('common.clear')}
                  </button>
                )}
          </>
        }
      />

      {loading ? (
        <TableSkeleton tk={tk} colWidths={[1, 3, 2, 1.5, 1.5, 1, 1.5, 0.8]} />
      ) : (
        <DataTable
          tk={tk}
          columns={columns}
          rows={currentItems}
          renderCell={renderCell}
          emptyMessage={
            searchTerm.trim() || selectedCategory || stockFilter !== 'all'
              ? t('inventory.tryDifferentSearch')
              : t('inventory.addFirstItem')
          }
        />
      )}

      <Pagination
        tk={tk}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* View item modal */}
      {viewingItem && (
        <div style={overlay}>
          <div style={modalBox}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: `1px solid ${tk.border}`,
            }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
                {t('inventory.productDetails')}
              </span>
              <button
                type="button"
                onClick={() => setViewingItem(null)}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: 6,
                  background: 'transparent', border: `1px solid ${tk.border}`,
                  color: tk.textSecondary, cursor: 'pointer',
                }}
              >
                <X size={14} />
              </button>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 72, height: 72, borderRadius: 10, flexShrink: 0,
                  background: tk.sunken, overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {viewingItem.image_url
                    ? <img src={getImageUrl(viewingItem.image_url)} alt={viewingItem.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Package size={28} style={{ color: tk.textMuted }} />
                  }
                </div>
                <div>
                  <p style={{ fontSize: 16, fontWeight: 600, color: tk.text, margin: '0 0 2px' }}>{viewingItem.name}</p>
                  <p style={{
                    fontSize: 12, color: tk.textSecondary, margin: '0 0 4px',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}>
                    {viewingItem.category_color && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: viewingItem.category_color, flexShrink: 0 }} />
                    )}
                    {viewingItem.category_name || t('inventory.uncategorized')}
                  </p>
                  {viewingItem.sku && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <p style={{ fontSize: 11, color: tk.textMuted, fontFamily: '"JetBrains Mono", monospace', margin: 0 }}>SKU: {viewingItem.sku}</p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(viewingItem.sku);
                          setSkuCopied(true);
                          setTimeout(() => setSkuCopied(false), 1500);
                        }}
                        style={{
                          width: 22, height: 22, borderRadius: 4,
                          background: 'transparent', border: 'none',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: tk.textMuted,
                        }}
                        title={t('common.copy')}
                      >
                        <Copy size={12} />
                      </button>
                      {skuCopied && (
                        <span style={{ fontSize: 11, color: tk.successText, fontWeight: 600 }}>{t('common.copied')}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  [t('inventory.costPrice'), `${parseFloat(viewingItem.cost_price ?? 0).toLocaleString()} ${t('currency.egp')}`],
                  [t('inventory.sellingPrice'), `${parseFloat(viewingItem.price ?? viewingItem.selling_price ?? 0).toLocaleString()} ${t('currency.egp')}`],
                  [t('inventory.profitMargin'), `${(parseFloat(viewingItem.price ?? viewingItem.selling_price ?? 0) - parseFloat(viewingItem.cost_price ?? 0)).toLocaleString()} ${t('currency.egp')}`],
                  [t('inventory.unitType'), viewingItem.unit_type === 'weight' ? t('inventory.perKg') : t('inventory.perPiece')],
                ].map(([label, value]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${tk.border}`, paddingBottom: 8 }}>
                    <span style={{ fontSize: 12, color: tk.textMuted }}>{label}</span>
                    <span style={{ fontSize: 12.5, color: tk.text }}>{value}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: tk.textMuted }}>{t('inventory.stockQuantity')}</span>
                  <span style={{
                    fontSize: 13, fontWeight: 700,
                    color: parseFloat(viewingItem.stock_quantity || 0) <= 0 ? tk.dangerText
                      : parseFloat(viewingItem.stock_quantity || 0) < lowStockThreshold ? tk.warning
                        : tk.successText,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                    <span style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      direction: 'ltr',
                      unicodeBidi: 'isolate',
                    }}>
                      {viewingItem.stock_quantity}
                    </span>
                    <span>{viewingItem.unit_type === 'weight' ? t('pos.kg') : t('pos.units')}</span>
                  </span>
                </div>
              </div>

              {canManageInventory && (
                <button
                  onClick={() => { setViewingItem(null); setEditingItem(viewingItem); }}
                  style={{
                    width: '100%', height: 38, borderRadius: 8,
                    background: tk.accent, border: 'none',
                    color: '#fff', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  }}
                >
                  <Edit size={14} />
                  {t('common.edit')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <ItemModal
          onClose={() => setShowAddModal(false)}
          onSave={() => { setShowAddModal(false); dispatch(fetchItems()); }}
        />
      )}
      {editingItem && (
        <ItemModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={() => { setEditingItem(null); dispatch(fetchItems()); }}
        />
      )}
      {showCategoryModal && (
        <CategoryManagementModal
          onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
          onSave={() => { setShowCategoryModal(false); dispatch(fetchCategories()); dispatch(fetchItems()); }}
        />
      )}
      {deletingItem && (
        <DeleteConfirmModal
          item={deletingItem}
          onClose={() => setDeletingItem(null)}
          onConfirm={async (id) => {
            try {
              await dispatch(deleteItem(id)).unwrap();
              dispatch(fetchItems());
            } catch (e) {
              showToast(getProductDeleteErrorMessage(e), "warning");
            }
          }}
        />
      )}
      {adjustingItem && (
        <QuickStockAdjustModal
          item={adjustingItem}
          onClose={() => setAdjustingItem(null)}
          onSaved={() => { setAdjustingItem(null); dispatch(fetchItems()); }}
        />
      )}
    </div>
  );
};

export default Inventory;
